/**
 * @module AnimatedTestimonial
 * @description A testimonial display component with smooth animations and interactive elements.
 * Features staggered animations, hover effects, and responsive design.
 * 
 * @typedef {import('@/types').Testimonial} Testimonial
 * 
 * @typedef {Object} AnimatedTestimonialProps
 * @property {Testimonial[]} testimonials - Array of testimonial objects to display
 * 
 * @example
 * ```jsx
 * const testimonials = [{
 *   name: "John D.",
 *   location: "West Palm Beach",
 *   text: "Exceptional service!",
 *   rating: 5
 * }];
 * 
 * <AnimatedTestimonial testimonials={testimonials} />
 * ```
 */

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * Card animation variants
 * @type {Object}
 */
const cardVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: (index) => ({ 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: index * 0.2,
      duration: 0.5,
      ease: [0.87, 0, 0.13, 1]
    }
  }),
  hover: {
    scale: 1.03,
    y: -5,
    transition: {
      duration: 0.3,
      ease: [0.87, 0, 0.13, 1]
    }
  },
  tap: {
    scale: 0.98
  }
};

/**
 * Rating animation variants
 * @type {Object}
 */
const ratingVariants = {
  hover: {
    scale: 1.1,
    transition: {
      duration: 0.2,
      ease: [0.87, 0, 0.13, 1]
    }
  }
};

/**
 * AnimatedTestimonial component
 * 
 * @param {AnimatedTestimonialProps} props - Component props
 * @returns {JSX.Element} Animated testimonial grid
 */
export function AnimatedTestimonial({ testimonials }) {
  if (!testimonials || testimonials.length === 0) {
    console.error('Testimonials array is required for AnimatedTestimonial');
    return (
      <div className="p-6 sm:p-8 rounded-2xl bg-red-50 text-red-600">
        <p>Error: Failed to load testimonial information</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {testimonials.map((testimonial, index) => (
        <motion.div
          key={index}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          whileTap="tap"
          custom={index}
          className="glass-card p-6 sm:p-8 relative overflow-hidden group hover:bg-white/10 hover:border-white/20 transition-all duration-300"
        >
          <div className="absolute top-4 right-4 transform -rotate-12 transition-transform group-hover:rotate-0 text-[#06B6D4] text-2xl opacity-80">
            <i className="fas fa-quote-right" />
          </div>

          <div className="space-y-4">
            <motion.div 
              className="flex items-center space-x-1"
              variants={ratingVariants}
            >
              {[...Array(testimonial.rating)].map((_, i) => (
                <motion.i
                  key={i}
                  className="fas fa-star text-[#06B6D4]"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                />
              ))}
            </motion.div>

            <motion.p 
              className="text-gray-200 text-lg leading-relaxed italic"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              "{testimonial.text}"
            </motion.p>

            <div className="pt-4 border-t border-white/10">
              <motion.div
                className="flex items-center space-x-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="w-10 h-10 rounded-full bg-[#7C3AED] flex items-center justify-center border border-white/20">
                  <span className="text-white font-semibold">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">{testimonial.name}</h4>
                  <p className="text-gray-400 text-sm">
                    <i className="fas fa-map-marker-alt mr-1 text-[#EC4899]" />
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            initial={false}
            animate={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
          />
        </motion.div>
      ))}
    </div>
  );
}

AnimatedTestimonial.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired
    })
  ).isRequired
}; 